// src/client/components/Menu.jsx
import React, { useEffect, useMemo, useState } from 'react';
import clientConfig from '../config/clientConfig';

// 🔠 Normalise une chaîne (ex: "Petite Faim" -> "petite_faim")
const norm = (s) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const Menu = ({ theme }) => {
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [categoriesTheme6, setCategoriesTheme6] = useState([]);
  const [degraded, setDegraded] = useState(false);
  const [loading, setLoading] = useState(true);

  const texts = clientConfig.texts?.menu || {};
  const navTexts = clientConfig.texts?.nav || {};

  // ✅ zéro texte en dur (même si vide)
  const title = texts.title || '';

  const categoriesDefault = texts.categories || [];

  const ui = texts.ui || {};
  const msgDegraded = ui.degradedMsg || '';
  const msgEmptyAll = ui.emptyAll || '';
  const msgEmptyCategory = ui.emptyCategory || '';
  const msgUnknownPrice = ui.unknownPrice || '';
  const ariaNavCats = ui.ariaNavCats || '';
  const ariaMenuSection = ui.ariaMenuSection || '';
  const labelAll = ui.allLabel || '';
  const showAllTab = !!ui.showAllTab;

  const clientKey =
    localStorage.getItem('selectedClient') ||
    clientConfig.clientKey ||
    'le_resto_local';

  // ——————————————————————————————————————————
  // 🧭 Charger les catégories dynamiques (MenuSettings) — theme6
  // ——————————————————————————————————————————
  useEffect(() => {
    const fallbackCategories = texts.categoriesTheme6 || [];

    fetch(`${process.env.REACT_APP_API_URL}/api/menu-settings?clientKey=${clientKey}`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.categoriesTheme6) && data.categoriesTheme6.length > 0) {
          setCategoriesTheme6(data.categoriesTheme6);
        } else {
          setCategoriesTheme6(fallbackCategories);
        }
      })
      .catch(() => setCategoriesTheme6(fallbackCategories));
  }, [texts.categoriesTheme6, clientKey]);

  // ——————————————————————————————————————————
  // 🍔 Charger les produits
  // ——————————————————————————————————————————
  useEffect(() => {
    const endpoint = theme === 'theme6' ? '/api/fastfood' : '/api/menu';
    const fallbackItems = clientConfig.texts?.menu?.items || [];
    const fullUrl = `${process.env.REACT_APP_API_URL}${endpoint}`;

    setLoading(true);

    fetch(fullUrl, { headers: { 'x-client-key': clientKey } })
      .then(async (res) => {
        if (!res.ok) throw new Error('server_error');
        const data = await res.json();

        if (Array.isArray(data)) {
          setItems(data);
          setDegraded(false);

          // Définir la catégorie active cohérente pour theme6
          if (theme === 'theme6' && categoriesTheme6.length > 0) {
            const firstNonEmpty =
              categoriesTheme6.find((lbl) =>
                data.some((it) => norm(it.categoryKey || it.category) === norm(lbl))
              ) || categoriesTheme6[0];
            setActiveCategory(firstNonEmpty);
          }
        } else {
          setItems(fallbackItems);
          setDegraded(true);
        }

        setLoading(false);
      })
      .catch(() => {
        setItems(fallbackItems);
        setDegraded(true);

        if (theme === 'theme6' && categoriesTheme6.length > 0 && !activeCategory) {
          setActiveCategory(categoriesTheme6[0]);
        }

        setLoading(false);
      });
  }, [theme, categoriesTheme6, clientKey]);

  // ——————————————————————————————————————————
  // ✅ Catégories calculées (si config vide) — autres thèmes + theme2
  // ——————————————————————————————————————————
  const computedCategories = useMemo(() => {
    if (Array.isArray(categoriesDefault) && categoriesDefault.length > 0) return categoriesDefault;

    const keys = Array.from(
      new Set(
        (items || [])
          .map((it) => it?.category)
          .filter(Boolean)
          .map((c) => norm(c))
      )
    );

    return keys.map((k) => ({
      key: k,
      label: k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
    }));
  }, [categoriesDefault, items]);

  // ——————————————————————————————————————————
  // ✅ Init activeCategory pour theme2 (APRÈS computedCategories)
  // ——————————————————————————————————————————
  useEffect(() => {
    if (theme !== 'theme2') return;
    if (activeCategory) return;
    const cats = computedCategories || [];
    const catsWithAll =
      showAllTab && labelAll
        ? [{ key: '__all__', label: labelAll }, ...cats]
        : cats;
    if (catsWithAll.length > 0) setActiveCategory(catsWithAll[0].key);
  }, [theme, activeCategory, computedCategories, showAllTab, labelAll]);

  // ——————————————————————————————————————————
  // 🎨 Rendu Thème 6 — inchangé (juste messages dynamiques)
  // ——————————————————————————————————————————
  const renderTheme6 = () => {
    const activeKey = norm(activeCategory);
    const filteredItems = items.filter(
      (item) => norm(item.categoryKey || item.category) === activeKey
    );

    return (
      <section id="menu" className="theme6-menu-section" aria-label={ariaMenuSection}>
        <h2 className="theme6-section-title mb-3">{title}</h2>

        {degraded && msgDegraded && (
          <div className="alert alert-warning mb-3 text-center" role="alert" aria-live="polite">
            {msgDegraded}
          </div>
        )}

        <nav className="theme6-menu-nav d-flex overflow-auto mb-4" aria-label={ariaNavCats}>
          {categoriesTheme6.map((cat) => (
            <button
              key={cat}
              className={`btn me-2 ${cat === activeCategory ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
              type="button"
              aria-current={cat === activeCategory ? 'true' : 'false'}
            >
              {cat}
            </button>
          ))}
        </nav>

        {loading && (
          <div className="menu-grid-theme6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="menu-card-theme6 skeleton">
                <div className="menu-card-img-wrapper" />
                <div className="menu-card-body">
                  <div className="skeleton-line" style={{ height: 14, width: '70%', marginBottom: 8 }} />
                  <div className="skeleton-line" style={{ height: 12, width: '90%', marginBottom: 6 }} />
                  <div className="skeleton-line" style={{ height: 12, width: '50%' }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && (
          <div className="menu-grid-theme6">
            {filteredItems.length === 0 && (
              <p style={{ opacity: 0.6 }}>
                {msgEmptyCategory ? msgEmptyCategory.replace('{category}', activeCategory || '') : ''}
              </p>
            )}

            {filteredItems.map((item) => {
              let displayPrice = '';
              if (item.variants && item.variants.length > 1) {
                const prices = item.variants.map((v) => Number(v.price) || 0);
                const min = Math.min(...prices);
                const max = Math.max(...prices);
                displayPrice =
                  min === max ? `${min.toFixed(2)} €` : `${min.toFixed(2)} € – ${max.toFixed(2)} €`;
              } else if (item.price) {
                displayPrice = `${Number(item.price).toFixed(2)} €`;
              } else {
                displayPrice = msgUnknownPrice || '';
              }

              return (
                <div key={item._id || item.name} className="menu-card-theme6">
                  <div className="menu-card-img-wrapper">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name || ''}
                        className="menu-card-img"
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                      />
                    )}
                    {item.featured && ui.featuredBadge && (
                      <div className="menu-card-badge">{ui.featuredBadge}</div>
                    )}
                  </div>

                  <div className="menu-card-body">
                    <h3 className="menu-card-title">{item.name}</h3>
                    {item.subText && <p className="menu-card-sub">{item.subText}</p>}
                    {item.description && (
                      <p className="menu-card-desc">
                        {item.description.length > 200 ? item.description.slice(0, 200) + '...' : item.description}
                      </p>
                    )}
                    <div className="menu-card-price">{displayPrice}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  // ——————————————————————————————————————————
  // 🧩 Render : Theme2 UX (nav catégories + grid)
  // ——————————————————————————————————————————
  const renderTheme2 = () => {
    const cats = computedCategories || [];
    const catsWithAll =
      showAllTab && labelAll
        ? [{ key: '__all__', label: labelAll }, ...cats]
        : cats;

    const filtered =
      activeCategory === '__all__' || !activeCategory
        ? items
        : items.filter((it) => norm(it?.category) === norm(activeCategory));

    // Grouped — calcul direct (pas de useMemo ici car on est dans une sous-fonction)
    const grouped = (() => {
      if (activeCategory !== '__all__') {
        return [
          {
            key: activeCategory,
            label:
              (cats.find((c) => norm(c.key) === norm(activeCategory))?.label) || '',
            items: filtered,
          },
        ];
      }

      const map = new Map();
      (items || []).forEach((it) => {
        const k = norm(it?.category);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(it);
      });

      return (cats || [])
        .map((c) => ({
          key: c.key,
          label: c.label,
          items: map.get(norm(c.key)) || [],
        }))
        .filter((g) => g.items.length > 0);
    })();

    return (
      <section id="menu" className="theme2-menu-section" aria-label={ariaMenuSection}>
        <div className="theme2-menu-header">
          <h2 className="theme2-section-title">{title}</h2>

          {/* ✅ nav sticky catégories */}
          {catsWithAll.length > 0 && (
            <nav className="theme2-menu-nav" aria-label={ariaNavCats}>
              <div className="theme2-menu-nav-inner">
                {catsWithAll.map((c) => {
                  const isActive = norm(c.key) === norm(activeCategory);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      className={`theme2-chip ${isActive ? 'active' : ''}`}
                      onClick={() => setActiveCategory(c.key)}
                      aria-current={isActive ? 'true' : 'false'}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </nav>
          )}
        </div>

        {degraded && msgDegraded && (
          <div className="alert alert-warning mb-3 text-center" role="alert" aria-live="polite">
            {msgDegraded}
          </div>
        )}

        {loading && (
          <div className="theme2-menu-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="theme2-menu-card skeleton">
                <div className="skeleton-line" style={{ height: 14, width: '70%', marginBottom: 10 }} />
                <div className="skeleton-line" style={{ height: 12, width: '92%', marginBottom: 6 }} />
                <div className="skeleton-line" style={{ height: 12, width: '45%' }} />
              </div>
            ))}
          </div>
        )}

        {!loading && (
          <>
            {(!items || items.length === 0) && (
              <p className="theme2-menu-empty" aria-live="polite">
                {msgEmptyAll}
              </p>
            )}

            {(items || items.length > 0) &&
              (grouped || []).map((group) => {
                if (!group?.items || group.items.length === 0) return null;

                return (
                  <div key={group.key} className="theme2-menu-group">
                    {group.label ? <h3 className="theme2-menu-category-title">{group.label}</h3> : null}

                    <div className="theme2-menu-grid">
                      {group.items.map((item) => {
                        const hasPrice =
                          item?.price !== undefined && item?.price !== null && item?.price !== '';
                        const priceText = hasPrice ? `${parseFloat(item.price).toFixed(2)} €` : (msgUnknownPrice || '');

                        return (
                          <article key={item._id || item.name} className="theme2-menu-card">
                            <header className="theme2-menu-card-top">
                              <h4 className="theme2-menu-name">{item?.name || ''}</h4>
                              {priceText ? <span className="theme2-menu-price">{priceText}</span> : null}
                            </header>

                            {item?.description ? (
                              <p className="theme2-menu-description">{item.description}</p>
                            ) : (
                              ui.emptyDesc ? (
                                <p className="theme2-menu-description theme2-menu-description--empty">
                                  {ui.emptyDesc}
                                </p>
                              ) : null
                            )}
                          </article>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

            {/* cas “catégorie vide” */}
            {items?.length > 0 &&
              activeCategory &&
              activeCategory !== '__all__' &&
              filtered?.length === 0 && (
                <p className="theme2-menu-empty" aria-live="polite">
                  {msgEmptyCategory ? msgEmptyCategory.replace('{category}', '') : ''}
                </p>
              )}
          </>
        )}
      </section>
    );
  };

  // ——————————————————————————————————————————
  // 🧩 Autres thèmes : rendu existant (juste suppression textes durs)
  // ——————————————————————————————————————————
  const renderByTheme = (categoryKey, categoryLabel, filteredItems) => {
    switch (theme) {
      case 'theme1': {
        if (!filteredItems || filteredItems.length === 0) return null;

        return (
          <div key={categoryKey} className="theme1-menu-block">
            <h3 className="menu-category-title">{categoryLabel}</h3>

            <div className="theme1-menu-grid">
              {filteredItems.map((item) => {
                let displayPrice = '';
                if (item?.variants && item.variants.length > 1) {
                  const prices = item.variants.map((v) => Number(v.price) || 0);
                  const min = Math.min(...prices);
                  const max = Math.max(...prices);
                  displayPrice =
                    min === max ? `${min.toFixed(2)} €` : `${min.toFixed(2)} € – ${max.toFixed(2)} €`;
                } else if (item?.price !== undefined && item?.price !== null && item?.price !== '') {
                  displayPrice = `${parseFloat(item.price).toFixed(2)} €`;
                } else {
                  displayPrice = msgUnknownPrice || '';
                }

                return (
                  <article
                    key={item._id || item.name}
                    className={`theme1-menu-card ${item.featured ? 'is-featured' : ''}`}
                  >
                    {item.featured && ui.featuredBadgeTheme1 && (
                      <div className="theme1-menu-badge">{ui.featuredBadgeTheme1}</div>
                    )}

                    <header className="theme1-menu-card-top">
                      <h4 className="theme1-menu-card-title">{item.name}</h4>
                      <span className={`theme1-menu-card-price ${displayPrice ? '' : 'theme1-menu-card-price--empty'}`}>
                        {displayPrice}
                      </span>
                    </header>

                    {item.subText && <p className="theme1-menu-card-sub">{item.subText}</p>}

                    {item.description ? (
                      <p className="theme1-menu-card-desc">
                        {item.description.length > 170 ? item.description.slice(0, 170) + '...' : item.description}
                      </p>
                    ) : (
                      ui.emptyDesc ? (
                        <p className="theme1-menu-card-desc theme1-menu-card-desc--empty">{ui.emptyDesc}</p>
                      ) : null
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        );
      }

      // ✅ theme2 géré par renderTheme2()
      case 'theme3':
        return (
          <div key={categoryKey}>
            <h3 className="theme3-menu-category-title">{categoryLabel}</h3>
            <div className="theme3-menu-grid">
              {filteredItems.map((item) => (
                <div key={item._id || item.name} className="theme3-menu-card">
                  <h3>{item.name}</h3>
                  <p>{item.description || ''}</p>
                  <button className="theme3-menu-btn" type="button">
                    {item?.price ? `${parseFloat(item.price).toFixed(2)} €` : (msgUnknownPrice || '')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'theme4':
        return (
          <div key={categoryKey}>
            <h3 className="theme4-menu-category-title">{categoryLabel}</h3>
            <div className="theme4-menu-grid">
              {filteredItems.map((item) => (
                <div key={item._id || item.name} className="theme4-menu-card">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name || ''}
                      className="theme4-menu-img"
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                    />
                  ) : null}
                  <h3>{item.name}</h3>
                  <p>{item.description || ''}</p>
                  <button className="theme4-menu-btn" type="button">
                    {item?.price ? `${parseFloat(item.price).toFixed(2)} €` : (msgUnknownPrice || '')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'theme5':
        return (
          <div key={categoryKey}>
            <h3 className="theme5-menu-category-title">{categoryLabel}</h3>
            <div className="theme5-menu-list">
              {filteredItems.map((item) => (
                <div key={item._id || item.name} className="theme5-menu-item">
                  <div className="theme5-menu-info">
                    <h3 className="theme5-menu-name">{item.name}</h3>
                    <p className="theme5-menu-price">
                      {item?.price ? `${parseFloat(item.price).toFixed(2)} €` : (msgUnknownPrice || '')}
                    </p>
                  </div>
                  {item.image ? (
                    <div className="theme5-menu-image-wrapper">
                      <img
                        src={item.image}
                        alt={item.name || ''}
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                      />
                      <span className="theme5-menu-price">
                        {item?.price ? `${parseFloat(item.price).toFixed(2)} €` : (msgUnknownPrice || '')}
                      </span>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ——————————————————————————————————————————
  // 🧩 Rendu principal
  // ——————————————————————————————————————————
  if (theme === 'theme6') return renderTheme6();
  if (theme === 'theme2') return renderTheme2();

  return (
    <section id="menu" className={`${theme}-menu-section`} aria-label={ariaMenuSection}>
      <h2 className={`${theme}-section-title`}>{title}</h2>

      {degraded && msgDegraded && (
        <div className="alert alert-warning mb-3 text-center" role="alert" aria-live="polite">
          {msgDegraded}
        </div>
      )}

      {loading && (
        <div className={theme === 'theme1' ? 'theme1-menu-grid' : 'menu-grid'}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={theme === 'theme1' ? 'theme1-menu-card skeleton' : 'menu-item skeleton'}>
              <div className="skeleton-line" style={{ height: 14, width: '70%', marginBottom: 10 }} />
              <div className="skeleton-line" style={{ height: 12, width: '92%', marginBottom: 6 }} />
              <div className="skeleton-line" style={{ height: 12, width: '45%' }} />
            </div>
          ))}
        </div>
      )}

      {!loading &&
        (() => {
          const blocks = (computedCategories || [])
            .map((cat) => {
              const key = cat?.key ?? '';
              const label = cat?.label ?? key;

              const filteredItems = items.filter((item) => norm(item?.category) === norm(key));

              return renderByTheme(key, label, filteredItems);
            })
            .filter(Boolean);

          if (blocks.length === 0) {
            return (
              <p className={`${theme}-menu-empty`} aria-live="polite">
                {msgEmptyAll}
              </p>
            );
          }

          return blocks;
        })()}
    </section>
  );
};

export default Menu;